<template>
  <div class="news bg-white fs-xl">
    <div class="container">
      <div class="page-padding" />
      <el-row justify="end">
        <el-col :xs="14" :sm="12" :lg="6">
          <SearchBar @on-search="onSearch" :loading="loading" />
        </el-col>
      </el-row>
      <el-skeleton :rows="14" animated v-if="loading" />
      <div class="container-content bg-white-1 mt-4" v-if="!loading">
        <div class="news-list">
          <el-empty
            v-if="!result.list.length && !loading"
            style="min-height: 280px"
          >
            <template #image>
              <el-icon class="fs-large text-black-2"><FolderDelete /></el-icon>
            </template>
            <template #description>
              <div style="fs-xxxl">
                未找到与“<span class="text-red">{{ query.keyword }}</span
                >”相关的内容
              </div>
            </template>
          </el-empty>
          <template v-for="(item, index) in result.list" :key="item.title">
            <el-card
              class="news-list-item bg-white-1"
              :body-style="{ padding: '1rem 0' }"
              :shadow="false"
              style="border: none"
            >
              <el-row :gutter="32">
                <el-col
                  v-if="item.cover"
                  :xs="24"
                  :sm="24 - colSpan(item.cover).sm"
                  class="pb-4 flex-one jc-center"
                >
                  <el-image
                    :src="FILE_URL + item.cover"
                    class="float-l mr-2 img"
                    style="max-width: 250px"
                    fit="scale-down"
                  />
                </el-col>
                <el-col v-bind="colSpan(item.cover)">
                  <component
                    :is="index === 0 ? 'div' : 'div'"
                    :value="index === 0 ? '置顶' : ''"
                  >
                    <p class="mb-4 fw-600 fs-xxl text-black">
                      <span
                        class="cursor-p text-hover underline-hover fs-xxxl"
                        @click="toDetail(item)"
                        :title="item.title"
                      >
                        {{ item.title }}
                      </span>
                      <span v-if="item.isTop === 1" class="text-red" style="">
                        [置顶]
                      </span>
                    </p>
                  </component>
                  <div
                    class="text-flow pos-r"
                    style="-webkit-line-clamp: 5; line-height: 1.6"
                    :title="item.content"
                    v-if="item.content"
                  >
                    <div class="indent text-justify">
                      {{ item.content }}
                    </div>
                    <span
                      class="text-active-color cursor-p pos-ab"
                      style="bottom: 0; right: 0; background-color: #f2f2f2"
                      title="查看详情"
                      @click="toDetail(item)"
                    >
                      [详情]
                    </span>
                  </div>
                  <div class="mt-4 text-black-2">
                    <span :title="`发布时间: ${item.publishTime}`">
                      {{ item.publishTime }}
                    </span>
                  </div>
                </el-col>
              </el-row>
            </el-card>
            <el-divider
              class="my-2"
              style="
                border-style: dotted;
                border-width: 1px;
                height: 0;
                background-color: rgba(0, 0, 0, 0);
              "
            />
          </template>
        </div>
        <div class="flex-one jc-center py-6 pb-3">
          <el-pagination
            background
            layout="total, prev, pager, next, jumper"
            :total="result.total"
            v-model:currentPage="query.pageNum"
            v-model:page-size="result.pageSize"
            @current-change="getNewsList"
            @size-change="getNewsList"
          />
        </div>
      </div>
      <div class="page-padding" />
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed, onMounted, toRaw } from 'vue'
import { FolderDelete } from '@element-plus/icons-vue'
import { useRouter } from 'vue-router'
import useSize from '@/hooks/useSize.js'

import { newsList } from '@/api/news.js'
import { setPV } from '@/api/config.js'

import { FILE_URL } from '@/config/constant.js'
import SearchBar from '@/components/search-bar/index.vue'

import { parseTime } from '@/utils'

const $router = useRouter()

const { xs } = useSize()

const pattern = new RegExp('<.+?>', 'g')
const loading = ref(true)

//跳转
const toDetail = (item) => {
  $router.push(`/news/${item.id}`)
}

//列表栅格数
const colSpan = computed(() => {
  return (status) => {
    return {
      sm: status ? 16 : 24,
      xs: 24
    }
  }
})

//检索
const onSearch = (keyword) => {
  query.keyword = keyword
  getNewsList()
}

//检索参数
const query = reactive({
  keyword: '',
  pageSize: 10,
  pageNum: 1
})

//result
const result = reactive({
  list: [],
  total: 0
})

const convertIdeogramToNormalCharacter = (val) => {
  const arrEntities = { lt: '<', gt: '>', nbsp: ' ', amp: '&', quot: '"' }
  return val.replace(/&(lt|gt|nbsp|amp|quot);/gi, function (all, t) {
    return arrEntities[t]
  })
}

const replaceRichText = (value) => {
  value = value.replace(/(\n)/g, '')
  value = value.replace(/(\t)/g, '')
  value = value.replace(/(\r)/g, '')
  value = value.replace(/<\/?[^>]*>/g, '')
  value = value.replace(/\s*/g, '')
  value = convertIdeogramToNormalCharacter(value)
  return value
}

/**
 * 获取新闻列表
 */
const getNewsList = async () => {
  window.scrollTo(0, 0)
  loading.value = true
  const res = await newsList(toRaw(query))
  loading.value = false
  result.list = res.records.map((item) => {
    return {
      ...item,
      publishTime: parseTime(item.publishTime),
      content: replaceRichText(item.content)
    }
  })
  result.total = res.total * 1
}

onMounted(async () => {
  getNewsList()
  await setPV()
})
</script>

<style scoped lang="scss"></style>

<style lang="scss">
.el-badge__content.is-fixed {
  top: 25%;
  right: calc(1px + var(--el-badge-size) / 2);
}
</style>
